import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Building2,
  Clock,
  Loader2,
  MapPin,
  Pin,
  Search,
  Star,
} from 'lucide-react';

import { cn } from '@/lib/utils';
import { supabase } from '@/lib/supabase';
import { useAdminAccounts, type AdminAccountRef } from '@/hooks/admin/useAdminAccounts';

/**
 * ⌘K / Ctrl-K account-finder command palette for the /admin section.
 *
 * Typeahead over ENABLED accounts (orders.customers, active=true) by name
 * and account number (qbo_customer_id). Superadmin RLS lets the caller read
 * the whole customers table. Selecting an account navigates to that account's
 * detail page (/admin/customers/<id>) and records it in recents.
 *
 * Empty-query state surfaces Pinned then Recents so the common accounts are
 * one keystroke + Enter away with no typing.
 *
 * Visual language matches AccountSwitcher (navy brix tokens, glass card,
 * shadow-card-deep) but this component is independent of it.
 */

const RESULT_CAP = 25;

interface PaletteRow {
  id: string;
  name: string;
  number: string;
  active: boolean;
  locationCount?: number;
}

/** Debounced typeahead over enabled customers. */
function useAccountSearch(query: string) {
  const q = query.trim();
  return useQuery<PaletteRow[]>({
    queryKey: ['admin', 'account-palette', q.toLowerCase()],
    queryFn: async () => {
      let builder = supabase
        .schema('orders')
        .from('customers')
        .select('id, name, qbo_customer_id, active')
        .eq('active', true)
        .order('name')
        .limit(RESULT_CAP + 1);

      if (q) {
        // Match name OR account number. qbo_customer_id is an INTEGER column,
        // so PostgREST `ilike` is invalid on it (no integer ~~* text operator).
        // Always match the name; when the query is all digits, additionally
        // match the account number with an exact integer equality.
        const safe = q.replace(/[%,()]/g, ' ');
        const digits = q.replace(/\D/g, '');
        const filters = [`name.ilike.%${safe}%`];
        if (digits) filters.push(`qbo_customer_id.eq.${digits}`);
        builder = builder.or(filters.join(','));
      }

      const { data, error } = await builder;
      if (error) throw error;
      return (data ?? []).map((r) => ({
        id: r.id as string,
        name: r.name as string,
        number: String(r.qbo_customer_id ?? ''),
        active: r.active as boolean,
      }));
    },
    staleTime: 30 * 1000,
  });
}

export interface AccountPaletteProps {
  open: boolean;
  onClose: () => void;
  /**
   * Where to send the user after they pick an account. Receives the
   * orders.customers row id. Defaults to the per-account detail page.
   */
  onSelectAccount?: (acct: AdminAccountRef) => void;
}

export function AccountPalette({ open, onClose, onSelectAccount }: AccountPaletteProps) {
  const navigate = useNavigate();
  const { recents, pinned, addRecent, togglePin, isPinned } = useAdminAccounts();

  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);

  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Reset state each time the palette opens; focus the input.
  useEffect(() => {
    if (open) {
      setQuery('');
      setDebounced('');
      setActiveIndex(0);
      // Defer focus until after the dialog paints.
      const t = setTimeout(() => inputRef.current?.focus(), 0);
      return () => clearTimeout(t);
    }
  }, [open]);

  // Debounce the query (~250ms).
  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 250);
    return () => clearTimeout(t);
  }, [query]);

  const { data: searchRows, isFetching } = useAccountSearch(open ? debounced : '');

  const hasQuery = debounced.length > 0;

  // Rows shown when there's a query: search results (capped).
  const queryRows: PaletteRow[] = useMemo(() => {
    if (!searchRows) return [];
    return searchRows.slice(0, RESULT_CAP);
  }, [searchRows]);
  const truncated = (searchRows?.length ?? 0) > RESULT_CAP;

  // Rows shown in the empty state: pinned, then recents (deduped against pinned).
  const emptyRows: { section: 'pinned' | 'recent'; acct: AdminAccountRef }[] = useMemo(() => {
    const pinnedIds = new Set(pinned.map((p) => p.id));
    const rows: { section: 'pinned' | 'recent'; acct: AdminAccountRef }[] = [];
    pinned.forEach((p) => rows.push({ section: 'pinned', acct: p }));
    recents
      .filter((r) => !pinnedIds.has(r.id))
      .forEach((r) => rows.push({ section: 'recent', acct: r }));
    return rows;
  }, [pinned, recents]);

  // Flat list of selectable accounts (for keyboard nav), in render order.
  const flatAccounts: AdminAccountRef[] = useMemo(() => {
    if (hasQuery) {
      return queryRows.map((r) => ({ id: r.id, name: r.name, number: r.number }));
    }
    return emptyRows.map((r) => r.acct);
  }, [hasQuery, queryRows, emptyRows]);

  // Keep the active index in range as the list changes.
  useEffect(() => {
    setActiveIndex((i) => (flatAccounts.length === 0 ? 0 : Math.min(i, flatAccounts.length - 1)));
  }, [flatAccounts.length]);

  const select = useCallback(
    (acct: AdminAccountRef) => {
      addRecent(acct);
      onClose();
      if (onSelectAccount) onSelectAccount(acct);
      else navigate(`/admin/customers/${acct.id}`);
    },
    [addRecent, navigate, onClose, onSelectAccount],
  );

  // Esc / arrow / enter handling on the dialog.
  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIndex((i) => (flatAccounts.length === 0 ? 0 : (i + 1) % flatAccounts.length));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIndex((i) =>
          flatAccounts.length === 0 ? 0 : (i - 1 + flatAccounts.length) % flatAccounts.length,
        );
        return;
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        const acct = flatAccounts[activeIndex];
        if (acct) select(acct);
      }
    },
    [activeIndex, flatAccounts, onClose, select],
  );

  // Scroll the active row into view.
  useEffect(() => {
    if (!open) return;
    const el = listRef.current?.querySelector<HTMLElement>(`[data-row-index="${activeIndex}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, open]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-start justify-center px-4 pt-[12vh]"
      role="dialog"
      aria-modal="true"
      aria-label="Find an account"
      onKeyDown={onKeyDown}
    >
      {/* Backdrop */}
      <button
        type="button"
        aria-label="Close account finder"
        className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm cursor-default"
        onClick={onClose}
        tabIndex={-1}
      />

      {/* Panel */}
      <div className="relative w-full max-w-xl rounded-2xl border border-brix-border bg-white shadow-card-deep overflow-hidden">
        {/* Search input */}
        <div className="flex items-center gap-2.5 px-4 border-b border-brix-border">
          <Search size={16} className="text-brix-muted shrink-0" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search accounts by name or number…"
            aria-label="Search accounts by name or number"
            className="flex-1 h-12 bg-transparent text-sm text-brix-ink placeholder:text-brix-muted focus:outline-none"
            autoComplete="off"
            spellCheck={false}
          />
          {isFetching && <Loader2 size={14} className="animate-spin text-brix-muted shrink-0" />}
          <kbd className="hidden sm:inline-flex items-center rounded border border-brix-border bg-brix-surface-alt px-1.5 py-0.5 text-[10px] font-mono text-brix-muted">
            esc
          </kbd>
        </div>

        {/* Results */}
        <div ref={listRef} className="max-h-[22rem] overflow-y-auto scrollbar-thin py-1.5">
          {hasQuery ? (
            queryRows.length === 0 ? (
              <EmptyMessage>
                {isFetching ? 'Searching…' : `No enabled accounts match “${debounced}”.`}
              </EmptyMessage>
            ) : (
              <>
                <SectionHeader icon={<Building2 size={11} />}>Accounts</SectionHeader>
                {queryRows.map((r, idx) => (
                  <AccountRow
                    key={r.id}
                    index={idx}
                    active={idx === activeIndex}
                    name={r.name}
                    number={r.number}
                    pinned={isPinned(r.id)}
                    onSelect={() => select({ id: r.id, name: r.name, number: r.number })}
                    onHover={() => setActiveIndex(idx)}
                    onTogglePin={() => togglePin({ id: r.id, name: r.name, number: r.number })}
                  />
                ))}
                {truncated && (
                  <div className="px-4 py-2 text-[11px] text-brix-muted">
                    Showing the first {RESULT_CAP}. Refine your search to narrow it down.
                  </div>
                )}
              </>
            )
          ) : emptyRows.length === 0 ? (
            <EmptyMessage>
              Start typing to find an account. Pinned and recent accounts will show up here.
            </EmptyMessage>
          ) : (
            <EmptyStateList
              emptyRows={emptyRows}
              activeIndex={activeIndex}
              isPinned={isPinned}
              onSelect={select}
              onHover={setActiveIndex}
              onTogglePin={togglePin}
            />
          )}
        </div>

        {/* Footer hint */}
        <div className="flex items-center gap-3 px-4 py-2 border-t border-brix-border bg-brix-surface-alt/50 text-[10px] text-brix-muted">
          <span className="inline-flex items-center gap-1">
            <kbd className="font-mono">↑↓</kbd> navigate
          </span>
          <span className="inline-flex items-center gap-1">
            <kbd className="font-mono">↵</kbd> open
          </span>
          <span className="inline-flex items-center gap-1">
            <Star size={10} /> pin/unpin
          </span>
        </div>
      </div>
    </div>
  );
}

function EmptyStateList({
  emptyRows,
  activeIndex,
  isPinned,
  onSelect,
  onHover,
  onTogglePin,
}: {
  emptyRows: { section: 'pinned' | 'recent'; acct: AdminAccountRef }[];
  activeIndex: number;
  isPinned: (id: string) => boolean;
  onSelect: (acct: AdminAccountRef) => void;
  onHover: (idx: number) => void;
  onTogglePin: (acct: AdminAccountRef) => void;
}) {
  const firstRecentIdx = emptyRows.findIndex((r) => r.section === 'recent');
  const hasPinned = emptyRows.some((r) => r.section === 'pinned');

  return (
    <>
      {emptyRows.map((row, idx) => {
        const showPinnedHeader = hasPinned && idx === 0;
        const showRecentHeader = idx === firstRecentIdx && firstRecentIdx !== -1;
        return (
          <div key={`${row.section}-${row.acct.id}`}>
            {showPinnedHeader && (
              <SectionHeader icon={<Pin size={11} />}>Pinned</SectionHeader>
            )}
            {showRecentHeader && (
              <SectionHeader icon={<Clock size={11} />}>Recent</SectionHeader>
            )}
            <AccountRow
              index={idx}
              active={idx === activeIndex}
              name={row.acct.name}
              number={row.acct.number}
              pinned={isPinned(row.acct.id)}
              onSelect={() => onSelect(row.acct)}
              onHover={() => onHover(idx)}
              onTogglePin={() => onTogglePin(row.acct)}
            />
          </div>
        );
      })}
    </>
  );
}

function SectionHeader({ icon, children }: { icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-1.5 px-4 pt-2 pb-1 text-[10px] font-semibold tracking-tight text-brix-muted uppercase">
      <span className="text-brix-primary">{icon}</span>
      {children}
    </div>
  );
}

function EmptyMessage({ children }: { children: React.ReactNode }) {
  return <div className="px-4 py-8 text-center text-sm text-brix-muted">{children}</div>;
}

function AccountRow({
  index,
  active,
  name,
  number,
  pinned,
  onSelect,
  onHover,
  onTogglePin,
}: {
  index: number;
  active: boolean;
  name: string;
  number: string;
  pinned: boolean;
  onSelect: () => void;
  onHover: () => void;
  onTogglePin: () => void;
}) {
  return (
    <div
      data-row-index={index}
      role="option"
      aria-selected={active}
      onMouseMove={onHover}
      onClick={onSelect}
      className={cn(
        'group mx-1.5 flex items-center gap-3 rounded-lg px-2.5 py-2 cursor-pointer',
        active ? 'bg-brix-primary-tint' : 'hover:bg-brix-primary-tint/50',
      )}
    >
      <Building2 size={15} className="text-brix-primary shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium text-brix-ink truncate">{name}</div>
        {number && (
          <div className="text-[11px] text-brix-muted inline-flex items-center gap-1">
            <MapPin size={10} /> Acct #{number}
          </div>
        )}
      </div>
      <button
        type="button"
        aria-label={pinned ? `Unpin ${name}` : `Pin ${name}`}
        aria-pressed={pinned}
        title={pinned ? 'Unpin' : 'Pin'}
        onClick={(e) => {
          e.stopPropagation();
          onTogglePin();
        }}
        className={cn(
          'shrink-0 rounded-md p-1 transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brix-primary/40',
          pinned
            ? 'text-brix-primary'
            : 'text-brix-muted opacity-0 group-hover:opacity-100 hover:text-brix-primary',
        )}
      >
        <Star size={14} className={pinned ? 'fill-current' : ''} />
      </button>
    </div>
  );
}
